import { useState, useEffect, useCallback, useRef } from "react";
import {
  ref as storageRef,
  listAll,
  getDownloadURL,
  getMetadata,
  deleteObject,
  uploadBytes,
} from "firebase/storage";
import { storage, STORAGE_READY } from "../../firebase";

/* ── helpers ─────────────────────────────────────────────────────── */
const fmt = bytes => {
  if (bytes < 1024)       return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
};
const fmtDate = iso =>
  iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—";

/* ── Load one folder (flat) ──────────────────────────────────────── */
async function loadFolder(folder) {
  const listResult = await listAll(storageRef(storage, folder));
  const items = await Promise.all(
    listResult.items.map(async item => {
      const [url, meta] = await Promise.all([
        getDownloadURL(item),
        getMetadata(item),
      ]);
      return {
        ref:      item,
        name:     item.name,
        fullPath: item.fullPath,
        url,
        size:     meta.size,
        updated:  meta.updated,
        type:     meta.contentType ?? "",
      };
    })
  );
  return items.sort((a, b) => new Date(b.updated) - new Date(a.updated));
}

/* ── Load folder recursively (for members with subdirs) ──────────── */
async function loadFolderRecursive(folderPath) {
  const listResult = await listAll(storageRef(storage, folderPath));
  const items = await Promise.all(
    listResult.items.map(async item => {
      const [url, meta] = await Promise.all([
        getDownloadURL(item),
        getMetadata(item),
      ]);
      return {
        ref:      item,
        name:     item.name,
        fullPath: item.fullPath,
        url,
        size:     meta.size,
        updated:  meta.updated,
        type:     meta.contentType ?? "",
      };
    })
  );
  /* Recurse into subdirectories */
  const subItems = await Promise.all(
    listResult.prefixes.map(prefix => loadFolderRecursive(prefix.fullPath))
  );
  return [...items, ...subItems.flat()].sort((a, b) => new Date(b.updated) - new Date(a.updated));
}

/* Pretty-print filename as member name: "Franco_Abal.jpeg" → "Franco Abal" */
function fileNameToMemberName(fileName) {
  return fileName.replace(/\.\w+$/, "").replace(/_(\d{2})$/, " ($1)").replace(/_/g, " ");
}

const FOLDERS = [
  { key: "officers",  label: "👤 Officer Photos",  path: "officers",   accept: "image/*" },
  { key: "posts",     label: "🖼️ Post Images",      path: "posts",      accept: "image/*" },
  { key: "downloads", label: "📥 Downloads",        path: "downloads",  accept: "image/*,.pdf,.doc,.docx", uploadable: true },
  { key: "members",   label: "👥 Members",          path: "landowners", recursive: true },
];

/* ── Component ───────────────────────────────────────────────────── */
export default function StorageTab({ flash, flashError }) {
  const [files,     setFiles]     = useState({ officers: [], posts: [], downloads: [], members: [] });
  const [loading,   setLoading]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting,  setDeleting]  = useState(null);   /* fullPath of item being deleted */
  const [folder,    setFolder]    = useState("officers");
  const [preview,   setPreview]   = useState(null);   /* { url, name } for lightbox */
  const [copied,    setCopied]    = useState(null);   /* fullPath of recently copied item */
  const uploadRef = useRef(null);

  /* ── Upload to downloads folder ─────────────────────────────────── */
  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { flashError("File is too large — maximum size is 10 MB."); return; }
    setUploading(true);
    try {
      const path = `downloads/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
      const sr   = storageRef(storage, path);
      await uploadBytes(sr, file, { contentType: file.type });
      const url  = await getDownloadURL(sr);
      flash(`✅ Uploaded "${file.name}" — URL copied to clipboard!`);
      await navigator.clipboard.writeText(url).catch(() => {});
      await refresh("downloads");
    } catch (e) {
      flashError("Upload failed: " + (e?.message ?? e?.code ?? "unknown error"));
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  /* ── Copy URL ────────────────────────────────────────────────────── */
  const copyUrl = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopied(item.fullPath);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      flash("URL: " + item.url);
    }
  };

  /* ── Load files ─────────────────────────────────────────────────── */
  const refresh = useCallback(async (target = null) => {
    if (!STORAGE_READY) return;
    setLoading(true);
    try {
      const folders = target ? [target] : ["officers", "posts", "downloads", "members"];
      const results = await Promise.all(folders.map(f => {
        const def = FOLDERS.find(fd => fd.key === f);
        return def?.recursive
          ? loadFolderRecursive(def.path)
          : loadFolder(def?.path ?? f);
      }));
      setFiles(prev => {
        const next = { ...prev };
        folders.forEach((f, i) => { next[f] = results[i]; });
        return next;
      });
    } catch (e) {
      console.error("Storage list error:", e);
      flashError("Could not load storage files: " + (e?.message ?? "unknown error"));
    } finally {
      setLoading(false);
    }
  }, [flashError]);

  useEffect(() => { refresh(); }, [refresh]);

  /* ── Delete ─────────────────────────────────────────────────────── */
  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?\n\nThis cannot be undone. Any posts or officer records still referencing this photo will show a broken image.`))
      return;
    setDeleting(item.fullPath);
    try {
      await deleteObject(item.ref);
      flash(`Deleted "${item.name}"`);
      setFiles(prev => ({
        ...prev,
        [folder]: prev[folder].filter(f => f.fullPath !== item.fullPath),
      }));
    } catch (e) {
      console.error("Delete error:", e);
      const code = e?.code ?? "";
      if (code === "storage/object-not-found")
        flashError("File not found — it may have already been deleted.");
      else if (code === "storage/unauthorized" || code === "storage/unauthenticated")
        flashError("Permission denied — make sure you are signed in as admin.");
      else
        flashError("Delete failed: " + (e?.message ?? code));
    } finally {
      setDeleting(null);
    }
  };

  /* ── Guard ───────────────────────────────────────────────────────── */
  if (!STORAGE_READY) {
    return (
      <div className="acard">
        <div className="atitle">☁️ Cloud Storage</div>
        <div className="empty-state" style={{ color: "#e65100" }}>
          Firebase Storage is not initialised. Set it up in the Firebase Console first,
          then reload this page.
        </div>
      </div>
    );
  }

  const current = files[folder] ?? [];
  const totalSize = current.reduce((s, f) => s + f.size, 0);

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <div>
      {/* Lightbox */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.82)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, cursor: "zoom-out",
          }}
        >
          <img
            src={preview.url}
            alt={preview.name}
            style={{ maxWidth: "90vw", maxHeight: "88vh", borderRadius: 12, boxShadow: "0 8px 40px rgba(0,0,0,.6)" }}
            onClick={e => e.stopPropagation()}
          />
          <div style={{ position: "absolute", bottom: "1.5rem", left: 0, right: 0, textAlign: "center",
                        color: "#fff", fontSize: ".85rem", opacity: .8, fontWeight: 600 }}>
            {preview.name} — click anywhere outside to close
          </div>
        </div>
      )}

      <div className="acard">
        {/* Header */}
        <div className="atitle">
          ☁️ Cloud Storage Manager
          <button
            className="btn-b"
            style={{ fontSize: ".78rem", padding: ".38rem .9rem" }}
            onClick={() => refresh()}
            disabled={loading}
          >
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>

        {/* Folder tabs */}
        <div style={{ display: "flex", gap: ".5rem", marginBottom: "1.4rem", borderBottom: "2px solid var(--br)", paddingBottom: ".85rem", flexWrap: "wrap" }}>
          {FOLDERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFolder(f.key)}
              style={{
                padding: ".42rem 1rem", borderRadius: 8, border: "1.5px solid",
                fontWeight: 700, fontSize: ".84rem", cursor: "pointer", transition: "all .15s",
                ...(folder === f.key
                  ? { background: "var(--g)", borderColor: "var(--g)", color: "#fff" }
                  : { background: "#fff", borderColor: "var(--br)", color: "var(--gr)" }),
              }}
            >
              {f.label}
              <span style={{ marginLeft: ".45rem", opacity: .7, fontWeight: 400 }}>
                ({(files[f.key] ?? []).length})
              </span>
            </button>
          ))}
        </div>

        {/* Members info banner */}
        {folder === "members" && (
          <div style={{
            border: "1.5px solid var(--g)", borderRadius: 10, padding: "1rem 1.4rem",
            background: "var(--gp)", marginBottom: "1.4rem",
          }}>
            <div style={{ fontWeight: 700, fontSize: ".88rem", color: "var(--gd)", marginBottom: ".35rem" }}>
              👥 Member Photos Storage
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--gr)", lineHeight: 1.7 }}>
              Photos are uploaded here automatically when you add or edit a member in the <strong>Landowners tab</strong>.<br/>
              Files are named after the member — e.g. <code style={{ background:"#fff", padding:".1rem .35rem", borderRadius:4 }}>Franco_Abal.jpeg</code> — making it easy to identify and delete outdated photos.<br/>
              Use <strong>🗑 Delete</strong> below to remove an old or duplicate photo. Then re-upload a new photo via the Landowners tab.
            </div>
          </div>
        )}

        {/* Downloads upload area */}
        {folder === "downloads" && (
          <div style={{
            border: "2px dashed var(--b)", borderRadius: 10, padding: "1.1rem 1.4rem",
            background: "var(--bp)", marginBottom: "1.4rem",
          }}>
            <div style={{ fontWeight: 700, fontSize: ".88rem", color: "var(--bd)", marginBottom: ".5rem" }}>
              📤 Upload File for Download
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--gr)", marginBottom: ".75rem" }}>
              Images (JPG, PNG, WEBP), PDFs, or Word documents — max 10 MB.<br/>
              After uploading, the file URL is automatically copied to your clipboard so you can paste it into a post.
            </div>
            <input
              ref={uploadRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              disabled={uploading}
              onChange={e => handleUpload(e.target.files[0])}
              style={{ fontSize: ".83rem" }}
            />
            {uploading && <div style={{ marginTop: ".5rem", fontSize: ".82rem", color: "var(--bd)", fontWeight: 600 }}>Uploading…</div>}
          </div>
        )}

        {/* Stats bar */}
        {current.length > 0 && (
          <div style={{ display: "flex", gap: "2rem", marginBottom: "1.2rem",
                        padding: ".7rem 1rem", background: "var(--c)", borderRadius: 10,
                        fontSize: ".83rem", fontWeight: 600, color: "var(--gr)" }}>
            <span>📁 {current.length} file{current.length !== 1 ? "s" : ""}</span>
            <span>💾 Total: {fmt(totalSize)}</span>
          </div>
        )}

        {/* File grid */}
        {loading ? (
          <div className="empty-state">Loading files…</div>
        ) : current.length === 0 ? (
          <div className="empty-state">
            No files in this folder yet.{" "}
            {folder === "officers"
              ? "Upload officer photos from the About tab."
              : "Upload post images from the Posts tab."}
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "1rem",
          }}>
            {current.map(item => (
              <div
                key={item.fullPath}
                style={{
                  border: "1.5px solid var(--br)", borderRadius: 12,
                  overflow: "hidden", background: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,.06)",
                  opacity: deleting === item.fullPath ? 0.45 : 1,
                  transition: "opacity .2s",
                }}
              >
                {/* Thumbnail or file icon */}
                {item.type.startsWith("image/") ? (
                  <div
                    onClick={() => setPreview({ url: item.url, name: item.name })}
                    style={{
                      width: "100%", paddingTop: "66%", position: "relative",
                      background: "var(--c)", cursor: "zoom-in",
                    }}
                  >
                    <img
                      src={item.url}
                      alt={item.name}
                      style={{
                        position: "absolute", inset: 0, width: "100%", height: "100%",
                        objectFit: "cover",
                      }}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div style={{
                    width: "100%", paddingTop: "66%", position: "relative",
                    background: "var(--c)",
                  }}>
                    <div style={{
                      position: "absolute", inset: 0, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontSize: "2.8rem",
                    }}>
                      {item.type.includes("pdf") ? "📄" : "📝"}
                    </div>
                  </div>
                )}

                {/* Info */}
                <div style={{ padding: ".65rem .75rem" }}>
                  <div style={{
                    fontSize: ".75rem", fontWeight: 700, color: "var(--dk)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    marginBottom: ".25rem",
                  }}
                    title={item.name}
                  >
                    {folder === "members" ? fileNameToMemberName(item.name) : item.name}
                  </div>
                  {folder === "members" && (
                    <div style={{ fontSize: ".63rem", color: "var(--gr)", marginBottom: ".15rem", fontFamily: "monospace" }}>
                      {item.name}
                    </div>
                  )}
                  <div style={{ fontSize: ".69rem", color: "var(--gr)", marginBottom: ".55rem" }}>
                    {fmt(item.size)} · {fmtDate(item.updated)}
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        flex: 1, textAlign: "center", padding: ".32rem .5rem",
                        borderRadius: 7, border: "1.5px solid var(--br)",
                        fontSize: ".72rem", fontWeight: 700, color: "var(--gr)",
                        textDecoration: "none", background: "#fff",
                      }}
                    >
                      🔗 Open
                    </a>
                    {(folder === "downloads" || folder === "members") && (
                      <button
                        onClick={() => copyUrl(item)}
                        style={{
                          flex: 1, padding: ".32rem .5rem", fontSize: ".72rem", borderRadius: 7,
                          border: "1.5px solid",
                          fontWeight: 700, cursor: "pointer",
                          ...(copied === item.fullPath
                            ? { background: "var(--g)", borderColor: "var(--g)", color: "#fff" }
                            : { background: "#fff", borderColor: "var(--b)", color: "var(--bd)" }),
                        }}
                      >
                        {copied === item.fullPath ? "✅ Copied!" : "📋 Copy URL"}
                      </button>
                    )}
                    <button
                      className="btn-r"
                      style={{ flex: 1, padding: ".32rem .5rem", fontSize: ".72rem", borderRadius: 7 }}
                      onClick={() => handleDelete(item)}
                      disabled={!!deleting}
                    >
                      {deleting === item.fullPath ? "…" : "🗑 Delete"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Warning */}
        {current.length > 0 && (
          <div style={{
            marginTop: "1.25rem", padding: ".7rem 1rem", borderRadius: 10,
            background: "#fff8e1", border: "1.5px solid #ffe082",
            fontSize: ".78rem", color: "#5d4037", fontWeight: 600,
          }}>
            ⚠️ Deleting a file here is permanent and cannot be undone. If the file is still
            referenced by an officer record or a post, it will show as a broken image on the
            website. Remove the reference first, then delete the file.
          </div>
        )}
      </div>
    </div>
  );
}
